import { ReactElement } from 'react';
import RichText from './Richtext';
import { IFragmentCtaFields } from 'types/contentful';
import type { Document as ContentfulDocument } from '@contentful/rich-text-types';

type HoursOfOperationProps = {
  locationHours: Record<string, { open: string; close: string }>;
  insuranceSlug?: string | undefined;
  footNotes?: string;
  daysForFootNotes?: string[];
  hoursInfoText?: string;
  hoursNotes?: string;
  interpreterAvailabilityText?: ContentfulDocument;
  hoursCta?: IFragmentCtaFields;
};

const allDays = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];

export default function HoursOfOperation({
  locationHours,
  footNotes,
  daysForFootNotes,
  hoursInfoText,
  hoursNotes,
  interpreterAvailabilityText,
}: HoursOfOperationProps): ReactElement {
  const noteCardClasses =
    'bg-white px-4 py-4 md:px-5 md:py-5 flex items-start gap-3 text-left text-lg leading-relaxed';

  return (
    <div className="flex h-full flex-col bg-muted text-primary-actual">
      <div className="flex h-full flex-col px-4 py-6 md:px-6 md:py-8 lg:px-5 xl:px-8">
        <h3 className="text-3xl text-left">Hours Of Operation</h3>

        <div className="mt-5 border-t border-secondary/30">
          {allDays.map((day) => {
            const hours = locationHours[day];
            return (
              <div
                key={day}
                className="flex items-center justify-between gap-4 border-b border-secondary/30 py-3 text-base md:text-lg"
              >
                <span className="capitalize leading-snug">
                  {daysForFootNotes?.includes(day) ? day + '*' : day}
                </span>
                <span className="text-right leading-snug">
                  {hours && hours.open && hours.close ? `${hours.open} - ${hours.close}` : 'Closed'}
                </span>
              </div>
            );
          })}
        </div>

        {(hoursInfoText || footNotes || hoursNotes || interpreterAvailabilityText) && (
          <div className="mt-6 space-y-2 md:space-y-3">
            {hoursInfoText && (
              <div className={noteCardClasses}>
                <InfoIcon />
                <p>{hoursInfoText}</p>
              </div>
            )}

            {footNotes && (
              <div className={noteCardClasses}>
                <InfoIcon />
                <p>{footNotes}</p>
              </div>
            )}

            {hoursNotes && (
              <div className={noteCardClasses}>
                <InfoIcon />
                <p>{hoursNotes}</p>
              </div>
            )}

            {interpreterAvailabilityText && (
              <div className={noteCardClasses}>
                <InfoIcon />
                <div className="flex-1">
                  <RichText
                    document={interpreterAvailabilityText}
                    classNames="text-inherit [&>p]:m-0 [&_p]:m-0"
                  />
                </div>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}

function InfoIcon(): ReactElement {
  return (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      width="24"
      height="24"
      viewBox="0 0 24 24"
      fill="none"
      className="mt-0.5 flex-shrink-0"
      aria-hidden="true"
    >
      <path
        d="M12 22C17.5228 22 22 17.5228 22 12C22 6.47715 17.5228 2 12 2C6.47715 2 2 6.47715 2 12C2 17.5228 6.47715 22 12 22Z"
        stroke="#00C1D4"
        strokeWidth="2"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
      <path
        d="M12 16V12"
        stroke="#00C1D4"
        strokeWidth="2"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
      <path
        d="M12 8H12.01"
        stroke="#00C1D4"
        strokeWidth="2"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}
